'use client';

import { SuggestResponseItem } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import {
  clusterByGrid as clusterByGridMethod,
  Feature
} from '@yandex/ymaps3-types/packages/clusterer';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState, useEffect } from 'react';

import CloseIcon from '@/fo/icons/base/icons/close24.svg';
import { UiKitDialogBase } from '@/fo/ui/kit/dialog/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { useMaps } from '@/fo/ui/mod/yandex/maps';

import { ClinicsList } from '../components/ClinicsList';
import { DoctorCard } from '../components/DoctorCard/DoctorCard';
import { MapContent } from '../components/MapContent/MapContent';
import { SearchLocationField } from '../components/SearchLocationField/SearchLocationField';
import { COMMON_LOCATION_PARAMS, DEFAULT_CLUSTER_GRID_SIZE } from '../consts';
import { useDoctorFeatures } from '../hooks/useDoctorFeatures';
import { DoctorFeature, Doctor, Clinic } from '../types';
import { getAddressFromSuggest } from '../utils/getAddressFromSuggest';
import { getBounds } from '../utils/getBounds';

import { styles } from './UiDoctorMapsBase.styles';

export interface UiDoctorMapsBaseProps {
  mode?: 'profile' | 'search';
  city?: string;
  doctors: Doctor[];
  selectedDoctor?: Doctor | null;
  selectedClinic?: Clinic | null;
  /**
   * Расстояние смещения для точек с одинаковыми координатами
   * По умолчанию 0.0001 (примерно 10 метров)
   */
  duplicateOffsetDistance?: number;
  initialZoom?: number;
}

export function UiDoctorMapsBase({
  mode = 'search',
  city,
  doctors,
  selectedDoctor,
  selectedClinic,
  duplicateOffsetDistance = 0.0001,
  initialZoom = 20
}: UiDoctorMapsBaseProps) {
  const slots = styles();
  const t = useTranslations();
  const mapsData = useMaps();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<DoctorFeature | null>(
    null
  );
  const [location, setLocation] = useState<YMapLocationRequest>({
    center: [37.617635, 55.755814],
    zoom: 10
  });
  const [searchLocation, setSearchLocation] = useState<{
    coordinates: [number, number];
    address: string;
  } | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const isDesktop = useMediaQuery('lg');

  const { doctorFeatures, hasDoctorsNearSearch } = useDoctorFeatures({
    doctors,
    searchLocation,
    offsetDistance: duplicateOffsetDistance
  });

  const gridSizedMethod = useMemo(() => {
    if (!mapsData?.clusterer) {
      return null;
    }

    const clusterByGrid = mapsData.clusterer
      .clusterByGrid as unknown as typeof clusterByGridMethod;

    return clusterByGrid({ gridSize: DEFAULT_CLUSTER_GRID_SIZE });
  }, [mapsData?.clusterer]);

  const handleLocationSelect = useCallback((coords: [number, number]) => {
    setLocation({ center: coords });
  }, []);

  const onMarkerClick = useCallback(
    () => (feature: DoctorFeature) => {
      setSelectedPoint(feature);
    },
    []
  );

  const onClusterClick = useCallback((features: Feature[]) => {
    const bounds = getBounds(
      features.map((feature: Feature) => [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ])
    );
    setLocation((prevLocation) => ({
      ...prevLocation,
      bounds,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  const onSuggestSelect = async (option: SuggestResponseItem | null) => {
    if (!mapsData?.search || !option) {
      setSearchLocation(null);
      setSearchValue('');
      return;
    }

    const addressText = getAddressFromSuggest(option);

    const coordinates = (await mapsData.search({ text: addressText }))?.[0]
      ?.geometry?.coordinates as [number, number] | undefined;

    if (coordinates) {
      handleLocationSelect(coordinates);
      setSearchLocation({ coordinates, address: addressText });
    } else {
      setSearchLocation(null);
    }

    setSearchValue(addressText);
  };

  const onMapMarkerClickWithDrawer = useCallback(
    (feature: DoctorFeature) => {
      if (isDesktop && selectedPoint?.id === feature.id) {
        setSelectedPoint(null);
        return;
      }

      onMarkerClick()(feature);
      if (!isDesktop) {
        setOpenDialog(true);
      }
      setLocation({
        center: feature.geometry.coordinates as [number, number]
      });
    },
    [onMarkerClick, isDesktop, selectedPoint?.id]
  );

  const onClinicClick = useCallback((clinic: DoctorFeature) => {
    setSelectedPoint(clinic);
    const coords = clinic.geometry.coordinates as [number, number];

    setLocation({ center: coords });
  }, []);

  const onMapClick = useCallback(() => {
    if (isDesktop && selectedPoint) {
      setSelectedPoint(null);
    }
    setSearchLocation(null);
  }, [isDesktop, selectedPoint]);

  const onSearchStart = useCallback(() => {
    setSelectedPoint(null);
    setOpenDialog(false);
  }, []);

  useEffect(() => {
    if (!selectedDoctor || !selectedClinic) return;

    if (!selectedClinic.longitude || !selectedClinic.latitude) return;

    const coords = [
      parseFloat(selectedClinic.longitude),
      parseFloat(selectedClinic.latitude)
    ] as [number, number];

    const doctorFeature = doctorFeatures.find(
      (feature) =>
        feature.properties.full_name === selectedDoctor.full_name &&
        feature.properties.clinic_address === selectedClinic.address
    );

    if (!doctorFeature) return;

    setSelectedPoint(doctorFeature);
    setOpenDialog(true);
    setLocation({ center: coords, zoom: 20 });
  }, [selectedDoctor, selectedClinic, doctorFeatures]);

  useEffect(() => {
    if (!city || !mapsData) return;
    if (selectedDoctor && selectedClinic) return;

    setSearchValue('');
    setSearchLocation(null);

    const centerMapOnCity = async () => {
      if (!mapsData.search) return;

      const result = await mapsData.search({
        text: city
      });

      const firstResult = result?.[0];
      const coordinates = firstResult?.geometry?.coordinates;

      if (coordinates) {
        const coords = coordinates as [number, number];
        setLocation({ center: coords, zoom: initialZoom });
      }
    };

    centerMapOnCity();
  }, [city, mapsData, selectedDoctor, selectedClinic, initialZoom]);

  useEffect(() => {
    if (!selectedPoint) return;

    const isSelectedPointExists = doctorFeatures.some(
      (feature) => feature.id === selectedPoint.id
    );

    if (!isSelectedPointExists) {
      setSelectedPoint(null);
      setOpenDialog(false);
    }
  }, [doctorFeatures, selectedPoint]);

  if (
    !mapsData?.reactifyApi ||
    !mapsData?.clusterer ||
    !gridSizedMethod ||
    !mapsData?.suggest
  ) {
    return;
  }

  const { YMapMarker } = mapsData.reactifyApi;

  return (
    <div className={slots.base()}>
      {mode === 'search' && (
        <div className={slots.searchLocationFieldWrapper()}>
          <SearchLocationField
            suggest={mapsData.suggest}
            onSelect={onSuggestSelect}
            city={city}
            className={slots.searchLocationField()}
            value={searchValue}
            onValueChange={setSearchValue}
            onSearchStart={onSearchStart}
          />
        </div>
      )}
      {mode === 'profile' && (
        <div className={slots.profilePanel()}>
          <div className={slots.profilePanelContent()}>
            <ClinicsList
              clinics={doctorFeatures}
              selectedClinic={selectedPoint}
              onClinicClick={onClinicClick}
            />
          </div>
        </div>
      )}
      <MapContent
        key="map-content"
        reactifyApi={mapsData.reactifyApi}
        clusterer={mapsData.clusterer}
        gridSizedMethod={gridSizedMethod}
        markers={doctorFeatures}
        selectedPoint={selectedPoint}
        location={location}
        isDesktop={isDesktop}
        onMarkerClick={onMapMarkerClickWithDrawer}
        onClusterClick={onClusterClick}
        onMapClick={onMapClick}
        mode={mode}
      >
        {selectedPoint && mode === 'search' && isDesktop && (
          <YMapMarker
            coordinates={selectedPoint.geometry.coordinates as [number, number]}
          >
            <div
              className={slots.desktopPopup()}
              onClick={(e) => e.stopPropagation()}
            >
              <DoctorCard doctor={selectedPoint.properties} />
              <button
                className={slots.desktopPopupCloseButton()}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPoint(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </YMapMarker>
        )}
        {searchLocation && !hasDoctorsNearSearch && mode === 'search' && (
          <YMapMarker coordinates={searchLocation.coordinates}>
            <div className={slots.noResultsMarker()}>
              <div className={slots.noResultsTitle()}>
                {t('Specialists have not been found')}
              </div>
              <div className={slots.noResultsAddress()}>
                {searchLocation.address}
              </div>
              <button
                className={slots.desktopPopupCloseButton()}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchLocation(null);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </YMapMarker>
        )}
      </MapContent>
      {selectedPoint && mode === 'search' && !isDesktop && (
        <UiKitDialogBase
          open={openDialog}
          onOpenChange={(open) => {
            setOpenDialog(open);
            if (!open) setSelectedPoint(null);
          }}
          modal={false}
          title={selectedPoint.properties.full_name}
          className="-left-36"
        >
          <DoctorCard doctor={selectedPoint.properties} />
        </UiKitDialogBase>
      )}
    </div>
  );
}
